import { Box, Typography, CircularProgress } from '@mui/material'
import type { TClientRequest, TFile } from '@modules/types'
import { FilesSection } from '../common/FilesSection'
import { SongsSection } from '../common/SongsSection'
import { CommentsSection } from '../common/CommentsSection'
import { LyricsDistributionSection } from './LyricsDistributionSection'
import { sectionDividerSx } from '../styles'

interface LyricsDetailsExpandedProps {
  expandedData: TClientRequest | undefined
  files?: TFile[]
  loading: boolean
}

export const LyricsDetailsExpanded = ({
  expandedData,
  files,
  loading,
}: LyricsDetailsExpandedProps) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress sx={{ width: '100%' }} />
      </Box>
    )
  }

  if (!expandedData) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Error loading details
        </Typography>
      </Box>
    )
  }

  const { additionalComments, songs, distributionDetails, id: requestId } = expandedData

  const hasFiles = files && files.length > 0
  const hasSongs = songs && songs.length > 0
  const hasDistributionDetails = distributionDetails && distributionDetails.length > 0
  const hasComments = Boolean(additionalComments)

  const sectionsCount = [hasFiles, hasSongs, hasDistributionDetails, hasComments].filter(
    Boolean,
  ).length

  return (
    <>
      {hasFiles && <FilesSection files={files} requestId={requestId} />}
      {hasFiles && (hasSongs || hasDistributionDetails || hasComments) && (
        <Box sx={sectionDividerSx} />
      )}

      {hasSongs && <SongsSection songs={songs} />}
      {hasSongs && (hasDistributionDetails || hasComments) && <Box sx={sectionDividerSx} />}

      {hasDistributionDetails && (
        <LyricsDistributionSection distributionDetails={distributionDetails} />
      )}
      {hasDistributionDetails && hasComments && <Box sx={sectionDividerSx} />}

      {hasComments && <CommentsSection comments={additionalComments} />}

      {sectionsCount === 0 && (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            No additional details available
          </Typography>
        </Box>
      )}
    </>
  )
}
